import { Link } from '@tanstack/react-router'
import { MonitorSmartphone, Search, UserPlus } from 'lucide-react'

import type { HomeWidgetProps } from '@/lib/home-widgets'
import { useAuth } from '@/lib/auth'
import { AGENT_PERMISSIONS, IAM_PERMISSIONS } from '@/lib/permissions'
import { usePermissions } from '@/lib/use-permissions'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/ui/button-variants'

import { useEntity } from '../../lib/entity-queries'

const linkClassName = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'focus-visible:ring-foreground')

export function WelcomeWidget({ entityId }: HomeWidgetProps) {
  const { user } = useAuth()
  const { can } = usePermissions()
  const { data: entity } = useEntity(entityId)
  // Provisioning falls back to the email when the identity provider sends no display name.
  const firstName = user?.name.includes('@') ? undefined : user?.name.trim().split(/\s+/)[0]
  const canAddAgent = can(AGENT_PERMISSIONS.installer) || can(AGENT_PERMISSIONS.pskManage)
  const canInvite = can(IAM_PERMISSIONS.manage)

  return (
    <div className="flex h-full flex-col justify-between gap-3">
      <div>
        <p className="text-foreground font-medium">{firstName ? `Welcome back, ${firstName}` : 'Welcome back'}</p>
        {entity && <p className="text-muted-foreground text-sm">{entity.name}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {canAddAgent && (
          <Link to="/$entityId/agents/add" params={{ entityId }} className={linkClassName}>
            <MonitorSmartphone aria-hidden />
            Add an agent
          </Link>
        )}
        {canInvite && (
          <Link to="/$entityId/invitations/send" params={{ entityId }} className={linkClassName}>
            <UserPlus aria-hidden />
            Invite a user
          </Link>
        )}
        <Link to="/$entityId/reports/query" params={{ entityId }} className={linkClassName}>
          <Search aria-hidden />
          Explore your data
        </Link>
      </div>
    </div>
  )
}
